import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PasswordChecklist from "react-password-checklist";
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setIsLoading(false);
      setMessage('Invalid token. Please request a new password reset link.');
      return;
    }

    setToken(tokenParam);
    validateToken(tokenParam);
  }, [searchParams]);

  const validateToken = async (tokenParam) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/native/validate-reset-password-token?token=${tokenParam}`
      );

      if (response.ok) {
        setIsValidToken(true);
      } else {
        setMessage('Invalid or expired token. Please request a new password reset link.');
      }
    } catch (error) {
      setMessage('An error occurred. Please try again later.');
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      setMessage('Please ensure all password requirements are met');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/native/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Token: token,
          NewPassword: password,
          BrowserInfo: navigator.userAgent
        }),
      });

      if (response.ok) {
        setIsSuccess(true);
        setMessage('Your password has been successfully changed.');
      } else {
        const data = await response.json();
        setMessage(data.message || 'Failed to update password. Please try again.');
      }
    } catch (error) {
      setMessage('An error occurred. Please try again later.');
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="login-container">
        <div className="login-form">
          <img src="https://afterschoolhq.com/Assets/img/ahq-logo-2-1-1.png" alt="Logo" className="login-logo" />
          <div>Loading...</div>
          <a href="#" className="reset-link" onClick={(e) => {
            e.preventDefault();
            navigate('/login');
          }}>Return to Login</a>
        </div>
      </div>
    );
  }

  if (!isValidToken && !isSuccess) {
    return (
      <div className="login-container">
        <div className="login-form">
          <img src="https://afterschoolhq.com/Assets/img/ahq-logo-2-1-1.png" alt="Logo" className="login-logo" />
          <div className="error-message">{message}</div>
          <a href="#" className="reset-link" onClick={(e) => {
            e.preventDefault();
            navigate('/login');
          }}>Return to Login</a>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="login-container">
        <div className="login-form">
          <img src="https://afterschoolhq.com/Assets/img/ahq-logo-2-1-1.png" alt="Logo" className="login-logo" />
          <div>{message}</div>
          <button onClick={() => navigate('/login')} className="login-button">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        <img src="https://afterschoolhq.com/Assets/img/ahq-logo-2-1-1.png" alt="Logo" className="login-logo" />
        <div className="password-field">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New Password"
            required
          />
        </div>
        <div className="password-field">
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm Password"
            required
          />
        </div>

        <div className="password-checklist">
          <PasswordChecklist
            rules={["minLength","specialChar","number","capital","match"]}
            minLength={8}
            value={password}
            valueAgain={confirmPassword}
            onChange={(isValid) => setIsPasswordValid(isValid)}
            messages={{
              minLength: "At least 8 characters long",
              specialChar: "Contains a special character",
              number: "Contains a number",
              capital: "Contains an uppercase letter",
              match: "Passwords match",
            }}
          />
        </div>

        {message && <div className="error-message">{message}</div>}
        
        <button type="submit" disabled={isLoading || !isPasswordValid}>
          {isLoading ? 'Updating...' : 'Reset Password'}
        </button>
        
        <a href="#" className="reset-link" onClick={(e) => {
          e.preventDefault();
          navigate('/login');
        }}>Return to Login</a>
      </form>
    </div>
  );
};

export default ResetPassword; 